import { useEffect } from 'react';

const NAVBAR_SELECTOR = '[data-landing-navbar]';
const BANNER_SELECTOR = '[data-landing-registration-banner]';
const STACK_BUFFER_PX = 12;

function pxToRem(px: number): string {
  const rootFontSize = parseFloat(
    getComputedStyle(document.documentElement).fontSize || '16',
  );
  return `${px / rootFontSize}rem`;
}

function applyLandingStickyStackOffsets(navbarPx: number, bannerPx: number) {
  const root = document.documentElement;
  const navbarRem = pxToRem(navbarPx);
  const bannerRem = bannerPx > 0 ? pxToRem(bannerPx) : '0px';
  const stackPx = navbarPx + bannerPx;

  root.style.setProperty('--landing-navbar-height', navbarRem);

  if (bannerPx > 0) {
    root.style.setProperty('--landing-registration-banner-height', bannerRem);
  } else {
    root.style.removeProperty('--landing-registration-banner-height');
  }

  root.style.setProperty('--landing-sticky-stack-height', pxToRem(stackPx));
  root.style.setProperty(
    '--landing-anchor-scroll-margin',
    pxToRem(stackPx + STACK_BUFFER_PX),
  );
}

function clearLandingStickyStackOffsets() {
  const root = document.documentElement;
  root.style.removeProperty('--landing-registration-banner-height');
  root.style.removeProperty('--landing-sticky-stack-height');
  root.style.removeProperty('--landing-anchor-scroll-margin');
}

export function useLandingStickyStackOffsets(trackBanner: boolean): void {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let navbarEl: HTMLElement | null = null;
    let bannerEl: HTMLElement | null = null;

    const measure = () => {
      const navbarPx = navbarEl
        ? Math.ceil(navbarEl.getBoundingClientRect().height)
        : 0;
      const bannerPx =
        trackBanner && bannerEl
          ? Math.ceil(bannerEl.getBoundingClientRect().height)
          : 0;

      if (navbarPx > 0) {
        applyLandingStickyStackOffsets(navbarPx, bannerPx);
      }
    };

    const resizeObserver = new ResizeObserver(measure);

    const connectElements = () => {
      const nextNavbar = document.querySelector<HTMLElement>(NAVBAR_SELECTOR);
      if (nextNavbar !== navbarEl) {
        if (navbarEl) {
          resizeObserver.unobserve(navbarEl);
        }
        navbarEl = nextNavbar;
        if (navbarEl) {
          resizeObserver.observe(navbarEl);
        }
      }

      const nextBanner = trackBanner
        ? document.querySelector<HTMLElement>(BANNER_SELECTOR)
        : null;
      if (nextBanner !== bannerEl) {
        if (bannerEl) {
          resizeObserver.unobserve(bannerEl);
        }
        bannerEl = nextBanner;
        if (bannerEl) {
          resizeObserver.observe(bannerEl);
        }
      }

      measure();
    };

    connectElements();

    const mutationObserver = new MutationObserver(connectElements);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', connectElements);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', connectElements);
      clearLandingStickyStackOffsets();
    };
  }, [trackBanner]);
}
